import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ExternalLink, Pagination, SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetTeamsLookupBffResponseTeam, PaginatedCollection, Repository, TagSetResource, UserResource, ApprovalPolicyResource } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { ApprovalPolicyTable } from "~/areas/library/components/ApprovalPolicies/ApprovalPolicyTable";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import { PageContent } from "~/components/PageContent/PageContent";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
export interface ApprovalPoliciesQueryParams {
    partialName?: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function approvalPoliciesPageLoader(repository: Repository, queryParams: ApprovalPoliciesQueryParams): Promise<ApprovalPoliciesPageLoaderData> {
    const approvalPolicies = getPaginatedApprovalPolicies(repository, { page: queryParams.page, pageSize: defaultPageSize });
    const projectTagSets = repository.TagSets.getAllByScopes(["Project"]);
    const environmentTagSets = repository.TagSets.getAllByScopes(["Environment"]);
    const teamsLookup = repository.Teams.lookupBff();
    const usersLookup = repository.Users.all();
    return {
        approvalPolicies: await approvalPolicies,
        projectTagSets: await projectTagSets,
        environmentTagSets: await environmentTagSets,
        teamsLookup: await teamsLookup,
        usersLookup: await usersLookup,
    };
}
export const approvalPoliciesPageTitle = "Approval Policies";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export interface ApprovalPoliciesPageLoaderData {
    approvalPolicies: PaginatedCollection<ApprovalPolicyResource>;
    projectTagSets: TagSetResource[];
    environmentTagSets: TagSetResource[];
    teamsLookup: GetTeamsLookupBffResponseTeam[];
    usersLookup: UserResource[];
}
interface ApprovalPoliciesPageProps {
    spaceId: string;
    loaderData: ApprovalPoliciesPageLoaderData;
    queryParams: ApprovalPoliciesQueryParams;
    setQueryParams: QueryParamValuesSetter<ApprovalPoliciesQueryParams>;
}
function primaryAction(spaceId: string) {
    const addApprovalPolicyPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: true, // TODO: needs a proper permission
        label: "Add Approval Policy",
        path: links.createApprovalPolicyPage.generateUrl({ spaceId }),
    };
    return addApprovalPolicyPageAction;
}
export function ApprovalPoliciesPage({ spaceId, loaderData, queryParams, setQueryParams }: ApprovalPoliciesPageProps) {
    const { result: policies } = usePaginatedApprovalPoliciesQuery(loaderData, queryParams);
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.partialName, (value) => setQueryParams({ partialName: value, page: 1 }), 200);
    const onPageChange = useCallback((page: number) => setQueryParams({ ...queryParams, page }), [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => setQueryParams({ ...queryParams, pageSize, page: 1 }), [queryParams, setQueryParams]);
    if (policies.TotalResults === 0) {
        return (<PortalEmptyStateOnboarding title={"Add an approval policy to manage your deployments"} description={"Start by creating an approval policy and scoping it to project and environment tags"} primaryAction={primaryAction(spaceId)} learnMore={<ExternalLink href={"UpdateMe"}>Update me please</ExternalLink>}/>);
    }
    const hideBottomPagination = policies.Items.length < 10;
    return (<PageContent header={{
            title: approvalPoliciesPageTitle,
            primaryAction: primaryAction(spaceId),
        }} pagination={{
            ui: (<Pagination label={"Approval Policies"} totalResults={policies.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={policies.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{ inputs: [<SearchInput autoFocus value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} accessibleName={"Search approval policies by name"} placeholder="Search by name"/>] }}>
            <BorderedContent>
                <ApprovalPolicyTable approvalPolicies={policies.Items} teamsLookup={loaderData.teamsLookup} usersLookup={loaderData.usersLookup} projectTagSets={loaderData.projectTagSets} environmentTagSets={loaderData.environmentTagSets}/>
            </BorderedContent>
        </PageContent>);
}
async function getPaginatedApprovalPolicies(repository: Repository, { page = 1, pageSize = defaultPageSize }) {
    return repository.ApprovalPolicies.list((page - 1) * pageSize, pageSize);
}
function usePaginatedApprovalPoliciesQuery(initialResult: ApprovalPoliciesPageLoaderData, queryParams: ApprovalPoliciesQueryParams) {
    const { page, pageSize, partialName } = queryParams;
    return useQuery((repository) => getPaginatedApprovalPolicies(repository, { page, pageSize }), [page, pageSize], "Get paginated approval policies", { initialResult: initialResult.approvalPolicies });
}
